import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowLeftIcon,
  PlayIcon,
  CheckIcon,
  BookOpenIcon,
} from "@heroicons/react/24/outline";
import { message } from "antd";
import ReactPlayer from "react-player";
import ReactLinkify from "react-linkify";
import axios from "axios";
import Navbar from "../../Components/Navbar/Navbar";
import { getUser } from "../../services/Authservice";
import { signInWithGoogle } from "../../apis/Signin";
import getVideos from "../../apis/getVideos";
import "./VideoPlayer.css";

export default function VideoPlayer() {
  const { state } = useLocation();
  const playlistID = state?.playlistID || localStorage.getItem("playlist-id");
  const tracking =
    (state?.tracking ?? localStorage.getItem("tracking") === "true") &&
    !!getUser();
  const uid = getUser()?.emails[0].value;
  const [videos, setVideos] = useState([]);
  const [info, setInfo] = useState([]);
  const [current, setCurrent] = useState("");
  const [status, setStatus] = useState("loading");
  const [saving, setSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const selected = videos.find((video) => video.videoId === current);
  const watched = videos.filter((video) => video.watched).length;

  useEffect(() => {
    if (!playlistID) {
      setStatus("empty");
      return;
    }
    localStorage.setItem("playlist-id", playlistID);
    localStorage.setItem("tracking", String(tracking));
    let active = true;
    setStatus("loading");
    const load = async () => {
      let lessons,
        playlistInfo = [];
      if (tracking) {
        const { data } = await axios.get(
          `/api/user/getvideos/${encodeURIComponent(uid)}/${playlistID}`,
          { timeout: 20000 },
        );
        lessons = data.videos;
        playlistInfo = data.playlistInfo;
      } else {
        lessons = (await getVideos(playlistID)).map(({ snippet }) => ({
          videoId: snippet.resourceId.videoId,
          title: snippet.title,
          description: snippet.description,
          watched: false,
        }));
      }
      if (!active) return;
      setVideos(lessons);
      setInfo(playlistInfo);
      setCurrent(
        (lessons.find((video) => !video.watched) || lessons[0])?.videoId || "",
      );
      setStatus(lessons.length ? "ready" : "empty");
    };
    load().catch(() => {
      if (active) setStatus("error");
    });
    return () => {
      active = false;
    };
  }, [playlistID, tracking, uid, attempt]);

  const markWatched = async (videoId, value) => {
    if (!tracking || saving) return;
    const updated = videos.map((video) =>
      video.videoId === videoId ? { ...video, watched: value } : video,
    );
    setSaving(true);
    try {
      await axios.post(
        `/api/user/updatevideos/${encodeURIComponent(uid)}/${playlistID}`,
        {
          playlistInfo: info,
          videos: updated,
          totalWatched: updated.filter((video) => video.watched).length,
        },
        { timeout: 20000 },
      );
      setVideos(updated);
    } catch {
      message.error(
        "Progress couldn’t be saved. Please try marking the lesson complete again.",
      );
    } finally {
      setSaving(false);
    }
  };
  const ended = () => {
    markWatched(current, true);
    const next =
      videos[videos.findIndex((video) => video.videoId === current) + 1];
    if (next) setCurrent(next.videoId);
  };

  return (
    <>
      <Navbar />
      <main className="page-width page-main player-page" id="main-content">
        <Link className="back-link" to={tracking ? "/dashboard" : "/"}>
          <ArrowLeftIcon />{" "}
          {tracking ? "Back to My learning" : "Back to courses"}
        </Link>
        {status === "loading" ? (
          <div className="empty-state surface" role="status">
            <span className="loading-ring" />
            <h3>Finding your next lesson.</h3>
            <p>Get comfortable. It’s time to learn.</p>
          </div>
        ) : status === "error" ? (
          <div className="empty-state surface" role="alert">
            <h3>This course couldn’t load.</h3>
            <p>Please try again in a moment.</p>
            <button
              className="button button-secondary"
              onClick={() => setAttempt(attempt + 1)}
            >
              Try again
            </button>
          </div>
        ) : status === "empty" ? (
          <div className="empty-state surface">
            <BookOpenIcon />
            <h3>No lesson to play just yet.</h3>
            <p>Choose a course to start your next chapter.</p>
            <Link className="button button-primary" to="/">
              Explore courses
            </Link>
          </div>
        ) : (
          <div className="player-grid">
            <section className="player-content">
              <div className="video-frame">
                <ReactPlayer
                  url={`https://www.youtube.com/watch?v=${current}`}
                  controls
                  width="100%"
                  height="100%"
                  onEnded={ended}
                />
              </div>
              <div className="lesson-heading">
                <p className="eyebrow">
                  LESSON{" "}
                  {videos.findIndex((video) => video.videoId === current) + 1}{" "}
                  OF {videos.length}
                </p>
                <h1>{selected?.title}</h1>
                {tracking && (
                  <button
                    className="button button-secondary"
                    disabled={saving}
                    onClick={() => markWatched(current, !selected?.watched)}
                  >
                    <CheckIcon />
                    {selected?.watched
                      ? "Completed · mark unwatched"
                      : "Mark as complete"}
                  </button>
                )}
              </div>
              <details className="surface lesson-description">
                <summary>About this lesson</summary>
                <ReactLinkify>
                  <p>
                    {selected?.description ||
                      "No description available for this lesson."}
                  </p>
                </ReactLinkify>
              </details>
              {!tracking && (
                <div className="surface player-signin">
                  <p>
                    {getUser()
                      ? "Save this course from Explore to track your progress."
                      : "Make this progress yours. Sign in to save courses and track your learning."}
                  </p>
                  {!getUser() && (
                    <button
                      className="button button-secondary"
                      onClick={signInWithGoogle}
                    >
                      Continue with Google
                    </button>
                  )}
                </div>
              )}
            </section>
            <aside className="surface lesson-list">
              <div className="lesson-list-heading">
                <h2>Your learning path</h2>
                <p>
                  {tracking
                    ? `${watched} of ${videos.length} lessons completed`
                    : `${videos.length} lessons · Your own pace`}
                </p>
                {tracking && (
                  <progress
                    value={watched}
                    max={videos.length}
                    aria-label="Course progress"
                  />
                )}
              </div>
              <ol>
                {videos.map((video, index) => (
                  <li
                    key={video.videoId}
                    className={current === video.videoId ? "selected" : ""}
                  >
                    <button
                      className="select-lesson"
                      aria-current={
                        current === video.videoId ? "true" : undefined
                      }
                      onClick={() => setCurrent(video.videoId)}
                    >
                      <span>
                        {video.watched ? (
                          <CheckIcon />
                        ) : current === video.videoId ? (
                          <PlayIcon />
                        ) : (
                          String(index + 1).padStart(2, "0")
                        )}
                      </span>
                      <span>{video.title}</span>
                    </button>
                    {tracking && (
                      <input
                        type="checkbox"
                        aria-label={`Mark ${video.title} complete`}
                        checked={video.watched}
                        disabled={saving}
                        onChange={(event) =>
                          markWatched(video.videoId, event.target.checked)
                        }
                      />
                    )}
                  </li>
                ))}
              </ol>
            </aside>
          </div>
        )}
      </main>
    </>
  );
}
